import React, { createContext, useContext, useLayoutEffect, useMemo, useRef } from 'react';

import type { Message, ConversationCallbacks } from './types';
import { WELCOME_MESSAGE_ID } from './welcome';

export interface ConversationContextValue {
  messages: Message[];
  callbacks: ConversationCallbacks;
  isStreaming: boolean;
}

const ConversationContext = createContext<ConversationContextValue | null>(null);

const defaultCallbacks: ConversationCallbacks = {
  onCopy: (_messageId, content) => void navigator.clipboard.writeText(content),
};

export function useConversation(): ConversationContextValue {
  const ctx = useContext(ConversationContext);
  if (!ctx) {
    throw new Error('useConversation must be used within a <Conversation> provider');
  }

  return ctx;
}

interface ConversationProviderProps {
  messages: Message[];
  callbacks: ConversationCallbacks;
  children: React.ReactNode;
}

export function ConversationProvider({ messages, callbacks, children }: ConversationProviderProps) {
  const isStreaming = messages.some((m) => m.status === 'streaming' && m.id !== WELCOME_MESSAGE_ID);

  // Keep callbacks in a ref so the stable wrappers below always call the latest
  // functions without needing to be recreated. useLayoutEffect (vs useEffect)
  // ensures the ref is updated before any user interaction can fire a callback.
  const callbacksRef = useRef({ ...defaultCallbacks, ...callbacks });
  useLayoutEffect(() => {
    callbacksRef.current = { ...defaultCallbacks, ...callbacks };
  });

  const stableCallbacks = useMemo<ConversationCallbacks>(
    () => ({
      onSend: (...args) => callbacksRef.current.onSend?.(...args),
      onStop: (...args) => callbacksRef.current.onStop?.(...args),
      onSuggestionClick: (...args) => callbacksRef.current.onSuggestionClick?.(...args),
      onActionClick: (...args) => callbacksRef.current.onActionClick?.(...args),
      onCitationClick: (...args) => callbacksRef.current.onCitationClick?.(...args),
      onAttachmentAdd: (...args) => callbacksRef.current.onAttachmentAdd?.(...args),
      onAttachmentRemove: (...args) => callbacksRef.current.onAttachmentRemove?.(...args),
      onCopy: (...args) => callbacksRef.current.onCopy?.(...args),
    }),
    [],
  );

  const value = useMemo(
    () => ({ messages, callbacks: stableCallbacks, isStreaming }),
    [messages, stableCallbacks, isStreaming],
  );

  return <ConversationContext.Provider value={value}>{children}</ConversationContext.Provider>;
}
